import { app } from "../../app.json";
import type { Config, tAPI } from "./API";

// Types
type UploadContext = {
  belongsTo: "Users" | "Companies" | "Institutions" | "Reports";
  ownerId: string;
  location: "documents" | "profile" | "reports" | "temporary";
};

type UploadUrlResponse = {
  url: string;
  key: string;
  fileId: string;
};

type ConfirmedFile = {
  _id: string;
  key: string;
  [k: string]: any;
};

type EditFileData = {
  altName?: string;
  comments?: string;
  expirationDate?: string | null;
  category?: string;
};

// API Storage static class — S3 presigned URL flow + v3 file management
const v3Config: Config = { vApi: "v3" };
const apiBase = "/storage";

export default class Storage {
  // --- Upload flow (3 steps) ---

  // Step 1: Request a presigned upload URL from backend
  static RequestUploadUrl(
    api: tAPI,
    file: File,
    context: UploadContext,
    config?: Config
  ): Promise<any> {
    return api.POST(
      `${apiBase}/upload-url`,
      {
        belongsTo: context.belongsTo,
        ownerId: context.ownerId,
        location: context.location,
        filename: file.name,
        contentType: file.type,
        size: file.size,
      },
      { ...v3Config, ...config }
    );
  }

  // Step 2: Upload file directly to S3 (no backend auth headers)
  static UploadToS3(url: string, file: File): Promise<Response> {
    if (app.localhost) {
      // Local dev: route through CRA proxy to avoid CORS with S3
      const s3Url = new URL(url);
      return fetch("/s3-upload" + s3Url.pathname + s3Url.search, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "X-S3-Target": s3Url.origin,
        },
      });
    }
    return fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
  }

  // Step 3: Confirm upload to backend
  static ConfirmUpload(
    api: tAPI,
    fileId: string,
    key: string,
    config?: Config
  ): Promise<any> {
    return api.POST(
      `${apiBase}/confirm`,
      { fileId, key },
      { ...v3Config, ...config }
    );
  }

  // --- Combined upload helper ---

  // Executes the full 3-step upload flow and returns { fileId, key, file }
  static async UploadFile(
    api: tAPI,
    file: File,
    context: UploadContext,
    config?: Config
  ): Promise<{ fileId: string; key: string; file: ConfirmedFile }> {
    // Step 1: get presigned URL
    const {
      data: { url, key, fileId },
    } = await Storage.RequestUploadUrl(api, file, context, config);

    // Step 2: upload to S3
    const s3Response = await Storage.UploadToS3(url, file);
    if (!s3Response.ok) {
      throw new Error(
        `S3 upload failed: ${s3Response.status} ${s3Response.statusText}`
      );
    }

    // Step 3: confirm
    const {
      data: { file: confirmedFile },
    } = await Storage.ConfirmUpload(api, fileId, key, config);

    return { fileId, key, file: confirmedFile };
  }

  // --- File management (v3) ---

  // Get a signed download URL for a single file
  static GetDownloadUrl(
    api: tAPI,
    fileId: string,
    config?: Config
  ): Promise<any> {
    return api.GET(`${apiBase}/files/${fileId}/url`, {
      ...v3Config,
      ...config,
    });
  }

  // List files for an entity/owner
  static ListFiles(
    api: tAPI,
    belongsTo: string,
    ownerId?: string,
    options?: { aboutExpire?: boolean },
    config?: Config
  ): Promise<any> {
    let query = `belongsTo=${belongsTo}`;
    if (ownerId) query += `&ownerId=${ownerId}`;
    if (options?.aboutExpire) query += `&aboutExpire=true`;
    return api.GET(`${apiBase}/files?populated=true&${query}`, {
      ...v3Config,
      ...config,
    });
  }

  // Edit file metadata
  static EditFile(
    api: tAPI,
    fileId: string,
    data: EditFileData,
    config?: Config
  ): Promise<any> {
    return api.PATCH(`${apiBase}/files/${fileId}`, data, {
      ...v3Config,
      ...config,
    });
  }

  // Delete a file
  static DeleteFile(api: tAPI, fileId: string, config?: Config): Promise<any> {
    return api.DELETE(`${apiBase}/files/${fileId}`, { ...v3Config, ...config });
  }
}
